import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Progress, StarRating } from '@/components/ui/Progress';
import { useGameStore } from '@/stores/gameStore';
import { useUserStore } from '@/stores/userStore';
import { LevelPlayground } from './LevelPlayground';

interface TutorialLevel {
  id: string;
  title: string;
  description: string;
  difficulty: string;
  estimatedTime: number;
  objectives: Objective[];
  hints: Hint[];
  tools: string[];
  completionMessage: string;
  xpReward: number;
}

interface Objective {
  id: string;
  description: string;
  required: boolean;
  tool: string;
  completed: boolean;
}

interface Hint {
  level: number;
  text: string;
  cost: number;
}

interface TutorialContainerProps {
  levelData: TutorialLevel;
}

export function TutorialContainer({ levelData }: TutorialContainerProps) {
  const navigate = useNavigate();
  const [objectives, setObjectives] = useState(levelData.objectives);
  const [showHint, setShowHint] = useState(false);
  const [currentHintIndex, setCurrentHintIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  const { hintsUsed, useHint: incrementHints, reset } = useGameStore();
  const { addXP, updateProgress, progress: userProgress } = useUserStore();

  const completedCount = objectives.filter((obj) => obj.completed).length;
  const totalCount = objectives.length;
  const progress = (completedCount / totalCount) * 100;

  // Reset state when level data changes
  useEffect(() => {
    setObjectives(levelData.objectives);
    setShowHint(false);
    setCurrentHintIndex(0);
    setIsCompleted(false);
  }, [levelData.id]);

  useEffect(() => {
    // Check if all required objectives are completed
    const allCompleted = objectives
      .filter((obj) => obj.required)
      .every((obj) => obj.completed);

    if (allCompleted && !isCompleted) {
      setIsCompleted(true);
      handleLevelComplete();
    }
  }, [objectives, isCompleted]);

  const handleLevelComplete = () => {
    // Calculate stars based on hints used
    let stars: 0 | 1 | 2 | 3 = 3;
    if (hintsUsed > 2) stars = 1;
    else if (hintsUsed > 0) stars = 2;

    // Check if level was already completed
    const previousProgress = userProgress.tutorialProgress.find(
      (p) => p.levelId === levelData.id
    );
    const wasAlreadyCompleted = previousProgress?.completed || false;

    console.log('🎯 Level completed:', levelData.id, {
      stars,
      xp: levelData.xpReward,
      wasAlreadyCompleted,
      awardingXP: !wasAlreadyCompleted
    });

    // Update user progress
    updateProgress(levelData.id, {
      completed: true,
      stars,
      completedAt: new Date(),
      attempts: 1,
    });

    // Award XP only if not previously completed
    if (!wasAlreadyCompleted) {
      addXP(levelData.xpReward);
    }
  };

  const handleHintClick = () => {
    if (currentHintIndex < levelData.hints.length) {
      setShowHint(true);
      incrementHints();
    }
  };

  const handleNextHint = () => {
    if (currentHintIndex < levelData.hints.length - 1) {
      setCurrentHintIndex(currentHintIndex + 1);
      incrementHints();
    }
  };

  const handleObjectiveComplete = (objectiveId: string) => {
    setObjectives((prev) =>
      prev.map((obj) =>
        obj.id === objectiveId ? { ...obj, completed: true } : obj
      )
    );
  };

  // Simulate objective completion for demo (remove in production)
  const handleSimulateAction = (tool: string) => {
    const objective = objectives.find(
      (obj) => obj.tool === tool && !obj.completed
    );
    if (objective) {
      handleObjectiveComplete(objective.id);
    }
  };

  const handleNextLevel = () => {
    // Extract level number and go to next
    const currentLevelNum = parseInt(levelData.id.split('-')[1]);
    const nextLevelNum = currentLevelNum + 1;

    // Reset game state for new level
    reset();

    // Navigate to next level
    navigate(`/tutorial/level-${nextLevelNum}`);
  };

  const handleBackToLevels = () => {
    reset();
    navigate('/tutorial');
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Level Header */}
          <Card variant="elevated">
            <CardHeader>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <Badge variant="info" size="sm" className="mb-2">
                    {levelData.difficulty}
                  </Badge>
                  <CardTitle className="text-2xl">{levelData.title}</CardTitle>
                </div>
                <div className="text-right">
                  <div className="text-primary-300 text-sm mb-1">
                    ~{levelData.estimatedTime} min
                  </div>
                  <StarRating stars={isCompleted ? 3 : 0} />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-primary-200 mb-4">{levelData.description}</p>
              <Progress value={progress} showLabel />
            </CardContent>
          </Card>

          {/* Objectives */}
          <Card variant="outline">
            <CardHeader>
              <CardTitle>Objectives</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {objectives.map((objective) => (
                  <div
                    key={objective.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      objective.completed
                        ? 'border-primary-600/50 bg-primary-950/30'
                        : 'border-dark-200 bg-dark-50'
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {objective.completed ? (
                        <svg
                          className="w-5 h-5 text-primary-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-dark-300" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p
                        className={
                          objective.completed
                            ? 'text-primary-300 line-through'
                            : 'text-primary-200'
                        }
                      >
                        {objective.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="default" size="sm">
                          {objective.tool}
                        </Badge>
                        {objective.required && (
                          <span className="text-xs text-primary-400">
                            Required
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Interactive Playground */}
          <Card variant="elevated">
            <CardHeader>
              <CardTitle>Playground</CardTitle>
            </CardHeader>
            <CardContent>
              <LevelPlayground
                starterCode={(levelData as any).starterCode}
                onAction={(action, data) => {
                  // Handle actions and check if objectives are completed
                  const objective = objectives.find(
                    (obj) => obj.tool.toLowerCase() === action.toLowerCase() && !obj.completed
                  );
                  if (objective) {
                    handleObjectiveComplete(objective.id);
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* Completion Message */}
          {isCompleted && (
            <Card variant="elevated" className="border-primary-600/50">
              <CardContent>
                <div className="text-center py-4">
                  <div className="text-4xl mb-2">🎉</div>
                  <h3 className="text-xl font-bold text-primary-400 mb-2">
                    Level Complete!
                  </h3>
                  <p className="text-primary-200 whitespace-pre-line mb-4">
                    {levelData.completionMessage}
                  </p>
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <Badge variant="success" size="lg">
                      +{levelData.xpReward} XP
                    </Badge>
                    <StarRating stars={3} size="lg" />
                  </div>
                  <div className="flex gap-3 justify-center">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleBackToLevels}
                    >
                      Back to Levels
                    </Button>
                    {parseInt(levelData.id.split('-')[1]) < 5 && (
                      <Button variant="primary" size="lg" onClick={handleNextLevel}>
                        Next Level →
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Tools */}
          <Card>
            <CardHeader>
              <CardTitle>Available Tools</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {levelData.tools.map((tool) => (
                  <Button
                    key={tool}
                    variant="secondary"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => handleSimulateAction(tool)}
                  >
                    <span className="mr-2">📝</span>
                    {tool}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Hints */}
          <Card>
            <CardHeader>
              <CardTitle>Hints</CardTitle>
            </CardHeader>
            <CardContent>
              {showHint ? (
                <div className="space-y-3">
                  <div className="p-3 bg-dark-100 rounded-lg border border-primary-600/30">
                    <p className="text-primary-200 text-sm">
                      {levelData.hints[currentHintIndex].text}
                    </p>
                  </div>
                  {currentHintIndex < levelData.hints.length - 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={handleNextHint}
                    >
                      Show Next Hint (-
                      {levelData.hints[currentHintIndex + 1].cost} XP)
                    </Button>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-primary-200/70 text-sm mb-3">
                    Need help? Use a hint to get started.
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={handleHintClick}
                  >
                    Show Hint (-{levelData.hints[0].cost} XP)
                  </Button>
                </div>
              )}
              <div className="mt-3 text-xs text-primary-300">
                Hints used: {hintsUsed}
              </div>
            </CardContent>
          </Card>

          {/* Info */}
          <Card>
            <CardContent className="text-sm text-primary-200/70">
              <p className="mb-2">
                💡 Complete all required objectives to finish the level.
              </p>
              <p>⭐ Use fewer hints to earn more stars!</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
